import { useQueries } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { isApiError, type Versioned } from '../api/client';
import { useApi } from '../api/context';
import {
  queryKeys,
  useAllCloudAccounts,
  useNetworkKinds,
  useNetworkPage,
  useProviders,
} from '../api/queries';
import type {
  AttachPolicy,
  CloudAccount,
  CreateNetworkRequest,
  Diagnostic,
  Network,
  NetworkValidation,
  SpokeToSpoke,
  TopologyKind,
} from '../api/types';
import { usePermission } from '../auth/context';
import { useToast } from '../components/toast';
import { Button, Dialog, Field, FieldMessages } from '../components/ui';
import { usePhone } from '../shell/breakpoints';
import { strings } from '../strings';
import { AddressPlanFields } from './lists/AddressPlanFields';
import { suggestSupernet } from './lists/cidr';
import { stepOfDiagnostic } from './lists/helpers';
import { NetworkDiagnosticList } from './lists/parts';
import {
  checkPlanForm,
  DEFAULT_PLAN_FORM,
  errorsAtFn,
  formToPlan,
  type AddressPlanForm,
} from './lists/planForm';
import { TopologyChoice } from './lists/TopologyChoice';
import { useNewNetworkValidation } from './lists/validation';
import { providerLabel, useManageNetworks, useNetworking } from './shared';

const c = strings.networks.lists.create;
const tt = strings.networks.topology;
type Step = 1 | 2 | 3 | 4 | 5;
const STEPS: readonly Step[] = [1, 2, 3, 4, 5];

interface ProviderForm {
  use: boolean;
  account: string;
  regions: string[];
  allowed: string[];
}

interface NewNetworkForm {
  name: string;
  description: string;
  providers: Record<string, ProviderForm>;
  plan: AddressPlanForm;
  topology: TopologyKind;
  // Hub-spoke only: New network has no firewall, so blocked is the default (via-hub needs the firewall, turned on in Settings).
  spokeToSpoke: SpokeToSpoke;
  attachPolicy: AttachPolicy;
}

function initialForm(providers: readonly string[]): NewNetworkForm {
  return {
    name: '',
    description: '',
    // With one provider offered, it starts chosen.
    providers: Object.fromEntries(
      providers.map((p) => [p, { use: providers.length === 1, account: '', regions: [], allowed: [] }]),
    ),
    plan: DEFAULT_PLAN_FORM,
    topology: 'isolated',
    spokeToSpoke: 'blocked',
    attachPolicy: 'operators',
  };
}

function usedProviders(form: NewNetworkForm): string[] {
  return Object.entries(form.providers)
    .filter(([, p]) => p.use)
    .map(([key]) => key);
}

function toRequest(form: NewNetworkForm): CreateNetworkRequest {
  const used = usedProviders(form);
  return {
    name: form.name.trim(),
    settings: {
      schemaVersion: 1,
      description: form.description.trim() || null,
      attachPolicy: form.attachPolicy,
      topology: {
        kind: form.topology,
        spokeToSpoke: form.topology === 'hub-spoke' ? form.spokeToSpoke : null,
        crossRegion: 'connected',
      },
      addressPlan: formToPlan(form.plan),
      providers: Object.fromEntries(
        used.map((p) => {
          const provider = form.providers[p];
          const allowed = [...new Set([provider.account, ...provider.allowed].filter(Boolean))];
          return [p, { regions: provider.regions, allowedAccounts: allowed }];
        }),
      ),
      // DNS and entry points are edited in Settings (spec 11 6.15.3).
      dns: { internalZone: null, privateLinkZones: 'auto' },
      entryPoints: [],
    },
    cloudAccounts: Object.fromEntries(used.map((p) => [p, form.providers[p].account])),
  };
}

/** Takes the dry run's preselection (U1.1) for providers whose connectivity account isn't chosen yet. */
function withPreselection(form: NewNetworkForm, validation: NetworkValidation): NewNetworkForm {
  let changed = false;
  const providers = { ...form.providers };
  for (const [provider, account] of Object.entries(validation.preselected.cloudAccounts)) {
    const current = providers[provider];
    if (!current || current.account) continue;
    changed = true;
    providers[provider] = {
      ...current,
      account,
      allowed: validation.preselected.allowedAccounts[provider] ?? [account],
    };
  }
  return changed ? { ...form, providers } : form;
}

function verificationText(account: CloudAccount): string {
  if (!account.lastVerification) return c.notVerified;
  return account.lastVerification.succeeded ? c.verified : c.verificationFailed;
}

/** "New network" (spec 11 6.15.3): five steps, a dry run on leaving each, preselection, Create. */
export function NewNetworkDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (network: Versioned<Network>) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const phone = usePhone();
  const ids = useId();
  const networking = useNetworking();
  const manage = useManageNetworks();
  const operate = usePermission('operate');
  const providers = useProviders();
  const accounts = useAllCloudAccounts(operate || manage);
  const kinds = useNetworkKinds();
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<NewNetworkForm>(() => initialForm(networking.providers));
  const [checked, setChecked] = useState<ReadonlySet<Step>>(new Set());
  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);
  const [general, setGeneral] = useState<{ step: Step; items: Diagnostic[] }>({ step: 1, items: [] });
  const [unfolded, setUnfolded] = useState(false);
  const [busy, setBusy] = useState(false);
  const dry = useNewNetworkValidation();
  const used = usedProviders(form);
  const id = (suffix: string) => `${ids}-${suffix}`;

  // The other networks' supernets, read when the address plan step opens, for the suggestion.
  const networks = useNetworkPage(null, step >= 3);
  const details = useQueries({
    queries: (networks.data?.items ?? []).map((n) => ({
      queryKey: queryKeys.network(n.id),
      queryFn: () => api.getNetwork(n.id),
      enabled: step >= 3,
    })),
  });
  const loadedAll = !!networks.data && details.every((d) => d.data || d.error);
  const otherSupernets = details.flatMap((d) => d.data?.data.settings.addressPlan.supernets ?? []).join();
  const suggestion = useMemo(
    () => (loadedAll ? suggestSupernet(otherSupernets ? otherSupernets.split(',') : []) : null),
    [loadedAll, otherSupernets],
  );
  if (step >= 3 && suggestion && !form.plan.supernetsTouched && form.plan.supernets.length === 0)
    setForm((f) => ({ ...f, plan: { ...f.plan, supernets: [suggestion] } }));

  // While the address plan step is open, its dry run shows the blocks the plan makes.
  const requestKey = JSON.stringify(toRequest(form));
  useEffect(() => {
    if (step !== 3 || form.plan.supernets.length === 0) return;
    const timer = setTimeout(() => void dry.run(toRequest(form)), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, requestKey]);

  const update = (patch: Partial<NewNetworkForm>) => setForm((f) => ({ ...f, ...patch }));
  const updateProvider = (provider: string, patch: Partial<ProviderForm>) =>
    setForm((f) => ({
      ...f,
      providers: { ...f.providers, [provider]: { ...f.providers[provider], ...patch } },
    }));

  const browserErrors = (s: Step): Record<string, string[]> => {
    const errors: Record<string, string[]> = {};
    if (s === 1 && !form.name.trim()) errors['/name'] = [c.nameRequired];
    if (s === 2) {
      if (used.length === 0) errors['/settings/providers'] = [c.noProviders];
      for (const p of used) {
        if (!form.providers[p].account) errors[`/cloudAccounts/${p}`] = [c.chooseAccount];
        if (!form.providers[p].regions.length) errors[`/settings/providers/${p}/regions`] = [c.regionsHelp];
      }
    }
    if (s === 3) return checkPlanForm(form.plan);
    return errors;
  };
  const shownBrowser: Record<string, string[]> = Object.assign(
    {},
    ...[...checked].map((s) => browserErrors(s)),
  );
  const visible = diagnostics.filter((d) => {
    const s = stepOfDiagnostic(d);
    return s !== null && checked.has(s as Step);
  });
  const errorsAt = errorsAtFn(shownBrowser, visible);
  const stepHasErrors = (s: Step) =>
    Object.keys(browserErrors(s)).length > 0 ||
    diagnostics.some((d) => d.severity === 'error' && stepOfDiagnostic(d) === s);
  const stepNotes = visible.filter((d) => stepOfDiagnostic(d) === step && d.severity !== 'error');

  const settle = (result: NetworkValidation, leaving: Step): Step | null => {
    setDiagnostics(result.diagnostics);
    setGeneral({ step: leaving, items: result.diagnostics.filter((d) => stepOfDiagnostic(d) === null) });
    setForm((f) => withPreselection(f, result));
    const blocking = result.diagnostics
      .filter((d) => d.severity === 'error')
      .map((d) => stepOfDiagnostic(d) ?? leaving)
      .filter((s) => s <= leaving);
    return blocking.length ? (Math.min(...blocking) as Step) : null;
  };

  const next = async () => {
    setChecked((current) => new Set([...current, step]));
    if (Object.keys(browserErrors(step)).length > 0) return;
    const result = await dry.run(toRequest(form));
    if (result) {
      const back = settle(result, step);
      if (back !== null) {
        setStep(back);
        return;
      }
    }
    setStep((step + 1) as Step);
  };

  const create = async () => {
    setChecked(new Set(STEPS));
    const invalid = STEPS.find((s) => Object.keys(browserErrors(s)).length > 0);
    if (invalid) {
      setStep(invalid);
      return;
    }
    setBusy(true);
    try {
      const created = await api.createNetwork(toRequest(form));
      toast.show({ tone: 'success', title: c.created(created.data.name) });
      onCreated(created);
    } catch (error) {
      if (isApiError(error)) {
        const found: Diagnostic[] = error.diagnostics.length
          ? error.diagnostics
          : [
              {
                severity: 'error',
                code: error.code,
                path: error.code === 'DUPLICATE_NAME' ? '/name' : '',
                message: error.detail ?? error.message,
              },
            ];
        if (error.status === 400 || error.status === 409) {
          setDiagnostics(found);
          const steps = found.map((d) => stepOfDiagnostic(d) ?? 5);
          const first = Math.min(...steps) as Step;
          setGeneral({ step: first, items: found.filter((d) => stepOfDiagnostic(d) === null) });
          setStep(first);
        } else toast.error(error);
      } else toast.error(error);
    } finally {
      setBusy(false);
    }
  };

  const accountList = accounts.data ?? null;
  const accountsOf = (provider: string) => (accountList ?? []).filter((a) => a.provider === provider);
  const accountName = (accountId: string) =>
    accountList?.find((a) => a.id === accountId)?.name ?? c.accountById(accountId);
  const providerName = (key: string) =>
    providers.data?.find((p) => p.key === key)?.displayName ?? providerLabel(key);
  const regionsOf = (key: string) => providers.data?.find((p) => p.key === key)?.regions ?? [];

  // Step 5 folds into one line when the preselection chose every used provider's accounts (U1.1).
  const preselected = dry.data?.preselected ?? null;
  const foldable =
    !!preselected &&
    used.length > 0 &&
    form.attachPolicy === 'operators' &&
    used.every((p) => {
      const account = preselected.cloudAccounts[p];
      const provider = form.providers[p];
      const allowed = [...new Set([provider.account, ...provider.allowed])].sort();
      const pre = [...new Set([account, ...(preselected.allowedAccounts[p] ?? [])])].sort();
      return !!account && provider.account === account && JSON.stringify(allowed) === JSON.stringify(pre);
    });
  const folded = foldable && !unfolded;

  const allocations = (dry.data?.allocations ?? []).reduce<Map<string, string[]>>((map, a) => {
    const key = `${a.provider} · ${a.region}`;
    map.set(key, [...(map.get(key) ?? []), c.allocationKind(a.kind, a.cidr)]);
    return map;
  }, new Map());

  const stepName = c.stepNames[step - 1];
  return (
    <Dialog
      title={c.title}
      onClose={onClose}
      wide
      className={phone ? 'dialog-sheet' : undefined}
      footer={
        <>
          {(dry.pending || busy) && (
            <span role="status" className="mr-auto self-center text-sm text-fg-muted">
              {busy ? c.creating : c.checking}
            </span>
          )}
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          {step > 1 && <Button onClick={() => setStep((step - 1) as Step)}>{c.back}</Button>}
          {step < 5 ? (
            <Button variant="primary" disabled={dry.pending} onClick={() => void next()}>
              {c.next}
            </Button>
          ) : (
            <Button variant="primary" disabled={busy} onClick={() => void create()}>
              {c.create}
            </Button>
          )}
        </>
      }
    >
      <ol aria-label={c.steps} className="stepper">
        {STEPS.map((s) => {
          const done = s < step;
          const content = (
            <>
              <span className="stepper-number" aria-hidden>
                {done ? <Check className="size-3.5" /> : s}
              </span>
              <span>{c.stepNames[s - 1]}</span>
            </>
          );
          return (
            <li
              key={s}
              aria-current={s === step ? 'step' : undefined}
              className={done ? 'is-done' : undefined}
            >
              {done ? (
                <button type="button" className="stepper-item" onClick={() => setStep(s)}>
                  {content}
                </button>
              ) : (
                <span className="stepper-item">{content}</span>
              )}
            </li>
          );
        })}
      </ol>

      <form
        noValidate
        aria-label={c.step(step, stepName)}
        onSubmit={(event) => {
          event.preventDefault();
          if (step < 5) void next();
          else void create();
        }}
      >
        <h3 className="mb-3 text-base font-semibold">{stepName}</h3>
        {checked.has(step) && stepHasErrors(step) && (
          <p role="alert" className="field-error mb-3 text-sm">
            {c.stepErrors}
          </p>
        )}
        {general.step === step && general.items.length > 0 && (
          <div className="mb-3">
            <p className="mb-1 text-sm">{c.otherProblems}</p>
            <NetworkDiagnosticList diagnostics={general.items} />
          </div>
        )}
        {stepNotes.length > 0 && (
          <div className="mb-3">
            <NetworkDiagnosticList diagnostics={stepNotes} />
          </div>
        )}

        {step === 1 && (
          <>
            <Field label={c.name} htmlFor={id('name')} errors={errorsAt('/name')} required>
              <input
                id={id('name')}
                className="input max-w-md"
                value={form.name}
                maxLength={64}
                autoComplete="off"
                aria-invalid={errorsAt('/name').length ? true : undefined}
                aria-describedby={errorsAt('/name').length ? `${id('name')}-error` : undefined}
                onChange={(e) => update({ name: e.target.value })}
              />
            </Field>
            <Field
              label={c.description}
              htmlFor={id('description')}
              help={c.descriptionHelp}
              errors={errorsAt('/settings/description')}
            >
              <input
                id={id('description')}
                className="input"
                value={form.description}
                maxLength={200}
                aria-describedby={`${id('description')}-help`}
                onChange={(e) => update({ description: e.target.value })}
              />
            </Field>
          </>
        )}

        {step === 2 && (
          <>
            {networking.providers.length === 0 && <p className="help">{c.noProviders}</p>}
            <FieldMessages messages={errorsAt('/settings/providers').filter(() => used.length === 0)} />
            {accounts.error && <p className="callout callout-info mb-3">{c.accountsNeedOperator}</p>}
            {networking.providers.map((provider) => {
              const p = form.providers[provider] ?? { use: false, account: '', regions: [], allowed: [] };
              const name = providerName(provider);
              const accountErrors = errorsAt(`/cloudAccounts/${provider}`);
              const regionErrors = errorsAt(`/settings/providers/${provider}/regions`);
              return (
                <fieldset key={provider} className="net-provider-block">
                  <legend className="sr-only">{name}</legend>
                  <label className="flex items-center gap-2 font-semibold">
                    <input
                      type="checkbox"
                      checked={p.use}
                      onChange={(e) => updateProvider(provider, { use: e.target.checked })}
                    />
                    {c.useProvider(name)}
                  </label>
                  {p.use && (
                    <div className="mt-2 grid gap-x-4 sm:grid-cols-2">
                      <Field
                        label={c.connectivityAccount}
                        htmlFor={id(`account-${provider}`)}
                        help={c.connectivityHelp}
                        errors={accountErrors}
                        required
                      >
                        {accountList ? (
                          <select
                            id={id(`account-${provider}`)}
                            className="input"
                            value={p.account}
                            aria-invalid={accountErrors.length ? true : undefined}
                            aria-describedby={`${id(`account-${provider}`)}-help`}
                            onChange={(e) => {
                              const account = e.target.value;
                              updateProvider(provider, {
                                account,
                                allowed: [
                                  ...new Set([account, ...p.allowed.filter((a) => a !== p.account)]),
                                ].filter(Boolean),
                              });
                            }}
                          >
                            <option value="">{c.chooseAccount}</option>
                            {accountsOf(provider).map((a) => (
                              <option key={a.id} value={a.id}>
                                {`${a.name} · ${verificationText(a)}`}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id={id(`account-${provider}`)}
                            className="input font-mono"
                            readOnly
                            value={p.account ? c.accountById(p.account) : ''}
                            aria-describedby={`${id(`account-${provider}`)}-help`}
                          />
                        )}
                      </Field>
                      <fieldset className="mb-3">
                        <legend className="label">{c.regions(name)}</legend>
                        <p className="help mb-1">{c.regionsHelp}</p>
                        <div
                          className="net-region-list"
                          aria-invalid={regionErrors.length ? true : undefined}
                        >
                          {regionsOf(provider).map((r) => (
                            <label key={r.name} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={p.regions.includes(r.name)}
                                onChange={(e) =>
                                  updateProvider(provider, {
                                    regions: e.target.checked
                                      ? [...p.regions, r.name]
                                      : p.regions.filter((x) => x !== r.name),
                                  })
                                }
                              />
                              <span>
                                {r.displayName}{' '}
                                <span className="font-mono text-xs text-fg-muted">{r.name}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                        <FieldMessages messages={regionErrors} />
                      </fieldset>
                    </div>
                  )}
                </fieldset>
              );
            })}
          </>
        )}

        {step === 3 && (
          <>
            <AddressPlanFields
              idPrefix={ids}
              value={form.plan}
              onChange={(plan) => update({ plan })}
              errorsAt={errorsAt}
              supernetNote={
                suggestion && form.plan.supernets.length === 1 && form.plan.supernets[0] === suggestion
                  ? strings.networks.lists.plan.suggested
                  : null
              }
            />
            <section aria-label={c.allocationsTitle} className="mt-2">
              <h4 className="label">{c.allocationsTitle}</h4>
              {allocations.size === 0 ? (
                <p className="help">{c.noAllocations}</p>
              ) : (
                <ul className="space-y-0.5 text-sm" data-testid="new-network-allocations">
                  {[...allocations].map(([key, blocks]) => (
                    <li key={key} className="font-mono">
                      {`${key}: ${blocks.join(', ')}`}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}

        {step === 4 && (
          <>
            <TopologyChoice
              topologies={kinds.data?.topologies ?? []}
              value={form.topology}
              onChange={(topology) => update({ topology })}
              label={c.topologyLegend}
            />
            {form.topology === 'hub-spoke' && (
              <fieldset className="mt-3" data-testid="new-network-spoke-to-spoke">
                <legend className="label">{tt.spokeToSpoke}</legend>
                {(['blocked', 'via-hub'] as const).map((value) => (
                  <label key={value} className="mb-1 flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="new-network-spoke-to-spoke"
                      className="mt-1"
                      checked={form.spokeToSpoke === value}
                      onChange={() => update({ spokeToSpoke: value })}
                    />
                    <span>
                      <span className="font-medium">{value === 'blocked' ? tt.blocked : tt.viaHub}</span>
                      {value === 'blocked' ? <> · {tt.blockedCost}</> : <> · {tt.viaHubLater}</>}
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
            <FieldMessages messages={errorsAt('/settings/topology')} />
          </>
        )}

        {step === 5 &&
          (folded ? (
            <p className="net-folded">
              <span>
                {c.folded(used.map((p) => accountName(form.providers[p].account)).join(', '), true)}
              </span>
              <Button onClick={() => setUnfolded(true)}>{c.change}</Button>
            </p>
          ) : (
            <>
              {used.map((provider) => {
                const p = form.providers[provider];
                const options = accountList
                  ? accountsOf(provider)
                  : [...new Set([p.account, ...p.allowed])]
                      .filter(Boolean)
                      .map((a) => ({ id: a, name: c.accountById(a) }));
                const errors = errorsAt(`/settings/providers/${provider}/allowedAccounts`);
                return (
                  <fieldset key={provider} className="mb-4">
                    <legend className="label">{c.allowedLegend(providerName(provider))}</legend>
                    <ul className="space-y-1">
                      {options.map((a) => {
                        const connectivity = a.id === p.account;
                        const helpId = id(`allowed-${provider}-${a.id}`);
                        return (
                          <li key={a.id}>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={connectivity || p.allowed.includes(a.id)}
                                disabled={connectivity}
                                aria-describedby={connectivity ? helpId : undefined}
                                onChange={(e) =>
                                  updateProvider(provider, {
                                    allowed: e.target.checked
                                      ? [...p.allowed, a.id]
                                      : p.allowed.filter((x) => x !== a.id),
                                  })
                                }
                              />
                              {a.name}
                            </label>
                            {connectivity && (
                              <p id={helpId} className="help ml-6">
                                {c.connectivityAlwaysAllowed}
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    <FieldMessages messages={errors} />
                  </fieldset>
                );
              })}
              <fieldset>
                <legend className="label">{c.whoMayAttach}</legend>
                {(
                  [
                    ['operators', c.operators, c.operatorsHelp],
                    ['network-admins', c.networkAdmins, c.networkAdminsHelp],
                  ] as const
                ).map(([value, label, help]) => (
                  <label key={value} className="mb-1 flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name={id('policy')}
                      className="mt-1"
                      checked={form.attachPolicy === value}
                      onChange={() => update({ attachPolicy: value })}
                    />
                    <span>
                      <span className="font-semibold">{label}</span>
                      <span className="block text-fg-muted">{help}</span>
                    </span>
                  </label>
                ))}
                <FieldMessages messages={errorsAt('/settings/attachPolicy')} />
              </fieldset>
            </>
          ))}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
